import { Box } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import useOnlineStatus, { OnlineStatus } from 'hooks/useOnlineStatus';

export const LoadMore = ({
  isFetching,
  hasNextPage = false,
  onEndReached,
  children,
  rootMargin,
  root,
}: {
  isFetching: boolean;
  hasNextPage?: boolean;
  onEndReached: () => void;
  children?: ReactNode;
  rootMargin: string;
  root?: Element | null;
}) => {
  const element = useRef(null);
  const [hasScrolled, setHasScrolled] = useState(true);
  const status = useOnlineStatus();

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(true);
    };

    const scroller = root ?? window;
    scroller.addEventListener('scroll', handleScroll);

    return () => {
      scroller.removeEventListener('scroll', handleScroll);
    };
  }, [root]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          status !== OnlineStatus.Offline &&
          entry.isIntersecting &&
          !isFetching &&
          hasNextPage &&
          (hasScrolled || entry.intersectionRatio === 1)
        ) {
          setHasScrolled(false);
          onEndReached();
        }
      },
      {
        root,
        rootMargin,
        threshold: 0,
      },
    );

    if (element.current) {
      observer.observe(element.current);
    }

    return () => observer.disconnect();
  }, [onEndReached, isFetching, hasNextPage, root, rootMargin, hasScrolled, status]);

  if (!hasNextPage || status === OnlineStatus.Offline) return null;
  return <Box ref={element}>{children}</Box>;
};
